const PENDING_POSITION_KEY = 'pendingTrackingPosition';
const LAST_POST_KEY = 'lastPost';

export function createWebTracking({
  sendPosition,
  onGpsStatus = () => {},
  onServerStatus = () => {},
  onStatusChange = () => {},
  onPosition = () => {},
  onError = () => {},
  intervalMs = 10000,
  minDistanceMeters = 15,
  heartbeatMs = 60 * 1000,
  maxPendingAgeMs = 2 * 60 * 1000
}) {
  let active = false;
  let timeoutId = null;
  let retryTimeoutId = null;
  let sending = false;
  let retryAttempt = 0;
  let pendingPosition = readPosition(localStorage, PENDING_POSITION_KEY);
  let lastSentPosition = readJson(sessionStorage, LAST_POST_KEY, null);

  function start() {
    if (active) return;

    active = true;
    restorePendingPosition();
    onStatusChange({
      mode: 'web',
      active: true,
      pendingPosition: serializePosition(pendingPosition)
    });
    flushPendingPosition();
    run();
  }

  function pause() {
    active = false;
    clearTimeout(timeoutId);
    clearTimeout(retryTimeoutId);
    timeoutId = null;
    retryTimeoutId = null;
    onStatusChange({ active: false, retryAt: null });
  }

  function destroy() {
    pause();
    window.removeEventListener('online', handleOnline);
  }

  function run() {
    if (!active) return;

    navigator.geolocation.getCurrentPosition(position => {
      // getCurrentPosition não pode ser cancelado. Esta verificação impede que
      // uma resposta atrasada reinicie o loop depois de pause().
      if (!active) return;

      onGpsStatus(true);
      onStatusChange({
        gpsStatus: 'available',
        lastCapturedPosition: serializePosition(position),
        lastCapturedAt: position.timestamp || Date.now()
      });
      onPosition(position);
      processPosition(position);
      scheduleNext();
    }, error => {
      if (!active) return;

      onGpsStatus(false);
      onStatusChange({
        gpsStatus: error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable',
        lastError: serializeError(error)
      });
      onError(error);

      if (error.code === error.PERMISSION_DENIED) {
        pause();
        return;
      }

      scheduleNext();
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  }

  function scheduleNext() {
    if (!active) return;

    clearTimeout(timeoutId);
    timeoutId = setTimeout(run, intervalMs);
  }

  function processPosition(position) {
    const shouldReplacePending = pendingPosition && isNewer(position, pendingPosition);
    const heartbeatDue = !lastSentPosition?.timestamp ||
      Date.now() - lastSentPosition.timestamp >= heartbeatMs;
    const shouldSend = !lastSentPosition || heartbeatDue ||
      calculateDistance(lastSentPosition, position.coords) >= minDistanceMeters;

    if (!shouldReplacePending && !shouldSend) return;

    if (!pendingPosition || isNewer(position, pendingPosition)) {
      pendingPosition = position;
      persistPosition(PENDING_POSITION_KEY, position);
      onStatusChange({ pendingPosition: serializePosition(position) });
    }

    flushPendingPosition();
  }

  async function flushPendingPosition() {
    if (!active || sending || retryTimeoutId || !pendingPosition) return;

    if (!isFresh(pendingPosition)) {
      clearPendingPosition();
      return;
    }

    sending = true;
    const positionBeingSent = pendingPosition;

    try {
      await sendPosition(positionBeingSent);

      lastSentPosition = {
        latitude: positionBeingSent.coords.latitude,
        longitude: positionBeingSent.coords.longitude,
        timestamp: Date.now()
      };
      sessionStorage.setItem(LAST_POST_KEY, JSON.stringify(lastSentPosition));
      retryAttempt = 0;
      clearTimeout(retryTimeoutId);
      retryTimeoutId = null;
      onServerStatus(true);
      onStatusChange({
        serverStatus: 'online',
        lastSentPosition: serializePosition(positionBeingSent),
        lastSentAt: Date.now(),
        retryAt: null
      });

      if (pendingPosition === positionBeingSent) {
        clearPendingPosition();
      }
    } catch (error) {
      onServerStatus(false, error);
      onStatusChange({
        serverStatus: 'offline',
        pendingPosition: serializePosition(pendingPosition),
        lastError: serializeError(error)
      });
      scheduleRetry();
    } finally {
      sending = false;

      if (active && pendingPosition && pendingPosition !== positionBeingSent) {
        flushPendingPosition();
      }
    }
  }

  function scheduleRetry() {
    if (!active || retryTimeoutId) return;

    const delays = [5000, 10000, 30000, 60000];
    const delay = delays[Math.min(retryAttempt, delays.length - 1)];
    retryAttempt += 1;
    onStatusChange({ retryAt: Date.now() + delay });

    retryTimeoutId = setTimeout(() => {
      retryTimeoutId = null;
      flushPendingPosition();
    }, delay);
  }

  function handleOnline() {
    if (!active) return;

    clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
    restorePendingPosition();
    flushPendingPosition();
  }

  function restorePendingPosition() {
    const storedPosition = readPosition(localStorage, PENDING_POSITION_KEY);

    if (!storedPosition || !isFresh(storedPosition)) {
      if (storedPosition) clearPendingPosition();
      return;
    }

    if (!pendingPosition || isNewer(storedPosition, pendingPosition)) {
      pendingPosition = storedPosition;
    }
  }

  function clearPendingPosition() {
    pendingPosition = null;
    localStorage.removeItem(PENDING_POSITION_KEY);
    onStatusChange({ pendingPosition: null });
  }

  function isFresh(position) {
    return position.timestamp && Date.now() - position.timestamp <= maxPendingAgeMs;
  }

  window.addEventListener('online', handleOnline);

  return { start, pause, destroy };
}

function calculateDistance(first, second) {
  const earthRadiusMeters = 6371000;
  const toRadians = value => value * Math.PI / 180;
  const latitudeDelta = toRadians(second.latitude - first.latitude);
  const longitudeDelta = toRadians(second.longitude - first.longitude);
  const a = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(first.latitude)) *
    Math.cos(toRadians(second.latitude)) *
    Math.sin(longitudeDelta / 2) ** 2;

  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function isNewer(first, second) {
  return (first.timestamp || 0) >= (second.timestamp || 0);
}

function persistPosition(key, position) {
  localStorage.setItem(key, JSON.stringify(serializePosition(position)));
}

function serializePosition(position) {
  if (!position) return null;

  return {
    timestamp: position.timestamp || Date.now(),
    coords: {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy ?? null,
      altitude: position.coords.altitude ?? null,
      speed: position.coords.speed ?? null,
      heading: position.coords.heading ?? null
    }
  };
}

function serializeError(error) {
  return {
    message: error?.body?.message || error?.message || error?.statusText || 'Erro no rastreamento.',
    status: error?.status || null,
    timestamp: Date.now()
  };
}

function readPosition(storage, key) {
  const position = readJson(storage, key, null);

  if (!position || !position.coords || position.coords.latitude == null || position.coords.longitude == null) {
    return null;
  }

  return position;
}

function readJson(storage, key, fallback) {
  try {
    return JSON.parse(storage.getItem(key)) || fallback;
  } catch (error) {
    storage.removeItem(key);
    return fallback;
  }
}
